import { trpc } from "@/lib/trpc";
import { Loader2 } from "lucide-react";
import { useLocation } from "wouter";
import { StatusBadge } from "@/components/StatusBadge";
import { DashboardChartCard } from "./DashboardChartCard";
import { DashboardKpiCard } from "./DashboardKpiCard";
import { DashboardPanel } from "./DashboardPanel";
import { DashboardSectionHeader } from "./DashboardSectionHeader";
import { LazySimpleDonutChart, LazySimpleTrendChart } from "./DashboardChartsLazy";
import { formatCurrency, formatVisitDateTime } from "./dashboardFormat";
import { formatSingaporeDate } from "@shared/timezone";

export function SalesServiceDashboardView() {
  const [, setLocation] = useLocation();
  const { data, isLoading } = trpc.serviceDashboard.sales.useQuery();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading dashboard…
      </div>
    );
  }

  const kpis = data?.kpis;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <DashboardKpiCard label="Upcoming Visits" value={kpis?.upcomingVisits ?? 0} />
        <DashboardKpiCard label="Visits This Month" value={kpis?.visitsThisMonth ?? 0} />
        <DashboardKpiCard label="Billed Visits" value={kpis?.billedVisits ?? 0} />
        <DashboardKpiCard label="Free / Warranty" value={kpis?.freeWarrantyVisits ?? 0} />
        <DashboardKpiCard
          label="Revenue From Visits"
          value={formatCurrency(kpis?.revenueFromVisits ?? 0)}
          className="col-span-2 md:col-span-1"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DashboardPanel className="p-4 lg:col-span-2">
          <DashboardSectionHeader
            title="Upcoming Service Visits for My Customers"
            subtitle="Scheduled visits on your accounts"
          />
          {data?.upcomingForCustomers?.length ? (
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                    <th className="pb-2 pr-4 font-medium">When</th>
                    <th className="pb-2 pr-4 font-medium">Customer / Site</th>
                    <th className="pb-2 pr-4 font-medium">Type</th>
                    <th className="pb-2 pr-4 font-medium">Technician</th>
                    <th className="pb-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data.upcomingForCustomers.map((v) => (
                    <tr
                      key={v.id}
                      className="hover:bg-slate-50/80 cursor-pointer"
                      onClick={() => setLocation(`/site-visits/id/${v.id}`)}
                    >
                      <td className="py-2.5 pr-4 text-xs text-slate-600 whitespace-nowrap">
                        {formatVisitDateTime(v.visitDate, v.arrivalWindow)}
                      </td>
                      <td className="py-2.5 pr-4 font-medium text-slate-900">{v.customerName}</td>
                      <td className="py-2.5 pr-4">
                        <span
                          className={`text-[10px] font-medium uppercase px-1.5 py-0.5 rounded ${
                            v.visitType === "urgent"
                              ? "bg-orange-100 text-orange-800"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {v.visitType === "urgent" ? "Urgent" : "Planned"}
                        </span>
                      </td>
                      <td className="py-2.5 pr-4 text-xs text-slate-600">
                        {v.technicianName ?? "—"}
                      </td>
                      <td className="py-2.5">
                        <StatusBadge status={v.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-xs text-slate-500 py-4">No upcoming visits for your customers.</p>
          )}
        </DashboardPanel>

        <DashboardPanel className="p-4">
          <DashboardSectionHeader title="Technicians You Work With Most" subtitle="This month" />
          {data?.techniciansMostWorkedWith?.length ? (
            <ul className="space-y-2">
              {data.techniciansMostWorkedWith.map((t) => (
                <li key={t.name} className="flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-900">{t.name}</span>
                  <span className="text-xs text-slate-500 tabular-nums">{t.count} visits</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-500">No technician visit data this month.</p>
          )}
        </DashboardPanel>

        <DashboardChartCard title="Billed vs Free Visits" subtitle="This month">
          <LazySimpleDonutChart
            data={[
              { name: "Billed", value: data?.billedVsFree?.billed ?? 0 },
              { name: "Free / Warranty", value: data?.billedVsFree?.freeWarranty ?? 0 },
            ]}
            colors={["#3b82f6", "#f97316"]}
          />
        </DashboardChartCard>

        <DashboardPanel className="p-4">
          <DashboardSectionHeader title="Top Customers by Visits" subtitle="This month" />
          {data?.topCustomers?.length ? (
            <ul className="space-y-2">
              {data.topCustomers.map((c) => (
                <li key={c.customerName} className="flex items-start justify-between gap-2 text-sm">
                  <span className="font-medium text-slate-900">{c.customerName}</span>
                  <span className="text-xs text-slate-500 text-right shrink-0">
                    {c.visitCount} visits
                    <br />
                    {formatCurrency(c.revenue)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-500">No customer visit data this month.</p>
          )}
        </DashboardPanel>

        <DashboardPanel className="p-4">
          <DashboardSectionHeader title="Recent Visits Scheduled By You" />
          {data?.recentScheduledByYou?.length ? (
            <ul className="divide-y divide-slate-100">
              {data.recentScheduledByYou.map((v) => (
                <li key={v.id}>
                  <button
                    type="button"
                    className="w-full text-left py-2.5 hover:bg-slate-50/80 rounded-md transition-colors"
                    onClick={() => setLocation(`/site-visits/id/${v.id}`)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-mono text-[11px] text-slate-500">{v.visitNumber}</p>
                        <p className="text-sm font-medium">{v.customerName}</p>
                        <p className="text-xs text-slate-500">
                          {formatSingaporeDate(v.visitDate)}
                        </p>
                      </div>
                      <StatusBadge status={v.status} />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-500">No visits scheduled by you yet.</p>
          )}
        </DashboardPanel>

        <DashboardPanel className="p-4">
          <DashboardSectionHeader title="Upcoming Visits You Scheduled" />
          {data?.upcomingScheduledByYou?.length ? (
            <ul className="space-y-2">
              {data.upcomingScheduledByYou.map((v) => (
                <li
                  key={v.id}
                  className="rounded-lg border border-slate-100 px-3 py-2 text-xs cursor-pointer hover:bg-slate-50/80"
                  onClick={() => setLocation(`/site-visits/id/${v.id}`)}
                >
                  <p className="font-medium text-slate-900">{v.customerName}</p>
                  <p className="text-slate-500">{formatVisitDateTime(v.visitDate, v.arrivalWindow)}</p>
                  {v.technicianName ? (
                    <p className="text-slate-400 mt-1">Tech: {v.technicianName}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-500">No upcoming visits you scheduled.</p>
          )}
        </DashboardPanel>

        <DashboardChartCard title="Visit & Revenue Trend" subtitle="Last 6 months" className="lg:col-span-2">
          <LazySimpleTrendChart
            data={data?.visitRevenueTrend ?? []}
            valueKey="visits"
            valueLabel="Visits"
            secondaryKey="revenue"
            secondaryLabel="Revenue"
          />
        </DashboardChartCard>
      </div>
    </div>
  );
}
